import axios from 'axios';
import * as API from '../url';
import axiosInstance from '../axiosInstance';
import {
  AgencyProfileDto,
  CompanyVideoUploadDto,
  GetAgencyProfileResponse,
  RateSetupDto,
  ResumeUploadPayload,
  TalentProfileDto,
  TalentVideoUploadDto,
  VideoDetails,
} from '../../types/agency';
import {
  AgencyMediaType,
  ExtractInfoPayload,
  GetUploadUrlPayload,
  GetUploadUrlResponse,
} from '../../types/app';
import { convertToBase64 } from '../../utils/helpers';

export const saveAgencyDetails = async (body: AgencyProfileDto) => {
  const res = await axiosInstance.post(API.SAVE_COMPANY_DETAILS, body);
  return res.data;
};

export const getAgencyProfile = async () => {
  const res = await axiosInstance.get<GetAgencyProfileResponse>(
    API.GET_AGENCY_DETAILS,
  );
  return res.data;
};

export const saveTalentDetails = async (body: TalentProfileDto) => {
  const res = await axiosInstance.post(API.SAVE_TALENT_DETAILS, body);
  return res.data;
};

export const getLanguages = async () => {
  const res = await axiosInstance.get(API.GET_LANGUAGES);
  return res.data;
};

export const getIndustries = async () => {
  const res = await axiosInstance.get(API.GET_INDUSTRIES);
  return res.data;
};

export const getCertificates = async () => {
  const res = await axiosInstance.get(API.GET_CERTIFICATES);
  return res.data;
};

export const getTechSpheres = async () => {
  const res = await axiosInstance.get(API.GET_TECH_SPHERES);
  return res.data;
};

export const getLangFromSpheres = async (sphereIds: number[] | string[]) => {
  const res = await axiosInstance.post(API.GET_LANG_FROM_SPHERES, sphereIds);
  return res.data;
};

export const getFrameWorksFromSpheres = async (
  sphereIds: number[] | string[],
) => {
  const res = await axiosInstance.post(
    API.GET_FRAMEWORKS_FROM_SPHERES,
    sphereIds,
  );
  return res.data;
};

export const getFrameWorks = async () => {
  const res = await axiosInstance.get(API.GET_FRAMEWORKS);
  return res.data;
};

export const uploadTalentVideo = async (body: TalentVideoUploadDto) => {
  const res = await axiosInstance.post(API.UPLOAD_TALENT_VIDEO, body);
  return res.data;
};

export const saveRateSetup = async (body: RateSetupDto) => {
  const res = await axiosInstance.post(API.AGENCY_SETUP, body);
  return res.data;
};

export const validateVimeoVideo = async (videoId: string) => {
  try {
    const res = await axios.get(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
        `https://vimeo.com/${videoId}`,
      )}`,
    );

    // Privacy check (Logic matches your component's check for 'privacy')
    if (res.data.domain_status_code === 403) {
      throw new Error(
        'Because of its privacy settings, this video cannot be played here.',
      );
    }

    return { valid: true, videoId, data: res.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // HTTP 404 on oEmbed = Video exists but is processing
      // Logic matches your component's check for 'not found'
      if (error.response.status === 404) {
        throw new Error('Video not found or is processing.');
      }
    }
    // Generic fallback
    throw error;
  }
};

export const getRateSetup = async (id: string) => {
  const res = await axiosInstance.get(
    `${API.AGENCY_SETUP}?associateId=${encodeURIComponent(id)}`,
  );
  return res.data;
};

export const uploadResumeForAutofill = async (
  file: File,
  associateId: number = 0,
) => {
  // 1. Convert File to Base64 String
  const base64Raw = await convertToBase64(file);

  const cleanBase64 = base64Raw.includes('base64,')
    ? base64Raw.split(',')[1]
    : base64Raw;

  // 2. ROBUST MIME TYPE DETECTION
  // Browsers sometimes send "" for .docx files. We must force it.
  let mimeType = file.type;
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.docx')) {
    // Force the official DOCX mime type
    mimeType =
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  } else if (fileName.endsWith('.pdf')) {
    mimeType = 'application/pdf';
  } else if (fileName.endsWith('.docx')) {
    mimeType = 'application/msword';
  }

  // 3. Construct the specific payload required
  const body: ResumeUploadPayload = {
    associateId: associateId,
    resume: {
      base64Str: cleanBase64,
      name: file.name,
      size: file.size, // Size in bytes
      // ✅ Use our robust mimeType, not the potentially empty file.type
      type: mimeType,
    },
  };

  console.log('📤 Upload Payload:', {
    ...body,
    resume: { ...body.resume, base64Str: '...hidden...' },
  });

  // 4. Post to the endpoint
  const res = await axiosInstance.post(API.TALENT_RESEUME, body);
  return res.data;
};

export const getUploadUrl = async (body: GetUploadUrlPayload) => {
  // Use axiosInstance if this API requires your Backend Token
  const res = await axiosInstance.post<GetUploadUrlResponse>(
    API.GET_UPLOAD_URL,
    body,
  );
  return res.data;
};

// Step 2: Upload directly to S3
// CRITICAL: We use 'axios' directly, not 'axiosInstance'
export const uploadToS3 = async (url: string, file: File) => {
  const res = await axios.put(url, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
  return res;
};

// Step 3: Extract Info
export const extractResumeInfo = async (body: ExtractInfoPayload) => {
  const res = await axiosInstance.post(API.EXTRACT_INFO, body);
  return res.data;
};

export const deleteTalentResume = async (id: number) => {
  const res = await axiosInstance.delete(
    `${API.TALENT_RESEUME}?associateId=${encodeURIComponent(id)}`,
  );
  return res.data;
};

export const getTalentResume = async (id: number) => {
  const res = await axiosInstance.get(
    `${API.TALENT_RESEUME}?associateId=${encodeURIComponent(id)}`,
  );
  return res.data;
};

export const validateExistingEmail = async (body: { email: string }) => {
  const res = await axiosInstance.post(API.VALIDATE_EXISTING_EMAIL, body);
  return res.data;
};

export const deleteAgencyMedia = async (payload: {
  uuid: string;
  fileType: AgencyMediaType;
}) => {
  const response = await axiosInstance.delete(API.DELETE_AGENCY_MEDIA, {
    params: payload,
  });
  return response.data;
};

export const uploadCompanyVideo = async (body: CompanyVideoUploadDto) => {
  const res = await axiosInstance.post(API.UPLOAD_AGENCY_VIDEO, body);
  return res.data;
};
